import { jwtDecode } from "jwt-decode";

import queryClient from "../../queryClient";
import { getApiBearerToken } from "../../services/auth/tokenService";

// True when a request made right now would carry an unexpired token. Without
// one a prefetch would only 401 on the way to the login redirect.
export const hasLiveSession = () => {
  try {
    const token = getApiBearerToken();
    if (!token) {
      return false;
    }
    const { exp } = jwtDecode(token);
    return !exp || exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

const isThenable = (value) => typeof value?.then === "function";

// prefetchQuery ignores `enabled`, so a disabled query is skipped here the same
// way the page's useQuery would skip it.
const prefetch = (query) => {
  if (query && (!("enabled" in query) || query.enabled)) {
    queryClient.prefetchQuery(query);
  }
};

const ignore = () => {};

const prefetchAll = (queries) => {
  for (const query of queries ?? []) {
    if (isThenable(query)) {
      query.then(prefetch, ignore);
    } else {
      prefetch(query);
    }
  }
};

// Route loader that starts a page's first queries alongside its code chunk
// instead of after the page mounts. It never awaits them: navigation is not
// held on the API, and the page's own useQuery picks up the in-flight request.
//
// `queriesFor(params, { request })` returns the page's query definitions, or a
// promise of them when they live in a module the loader imports on demand. An
// entry may itself be a promise, for a query keyed on another query's data.
// Prefetching is best effort: a failure here only costs the head start.
export const prefetchLoader = (queriesFor) => (args) => {
  if (!hasLiveSession()) {
    return null;
  }
  try {
    const queries = queriesFor(args.params, args);
    if (isThenable(queries)) {
      queries.then(prefetchAll, ignore);
    } else {
      prefetchAll(queries);
    }
  } catch {
    // A malformed URL shouldn't turn into a route error; the page handles it.
  }
  return null;
};

export const searchParamsOf = (request) => new URL(request.url).searchParams;
